/**
 * 食品追加パネル（共通コンポーネント）
 *
 * 食事編集/テンプレート編集ダイアログから使用される。
 * AI解析・お気に入り(テンプレート)・DB検索の3モードで食品を追加する。
 * DB検索は既存の FoodSearchSection を利用。
 */

import { Database, Loader2, Sparkles, Star } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { parseFoodText } from '@/api/services/nutrition'
import type { FoodTemplate, MealItemInput } from '@/api/types/nutrition'
import { PhotoInput } from '@/components/shared/photo-input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useSubmitLock } from '@/hooks/useSubmitLock'

import type { FoodCartItem } from './cart-utils'
import { FoodSearchSection } from './food-search-section'
import { TemplateFavoritesSection } from './template-favorites-section'

export type AddMode = 'ai' | 'template' | 'db'

type FoodAddPanelProps = {
  openModes: Set<AddMode>
  onToggleMode: (mode: AddMode) => void
  onAdd: (items: MealItemInput[]) => void
  /** テンプレート編集時に自分自身を除外するためのID */
  excludeTemplateId?: number
  /** テンプレート編集ボタン押下時のコールバック */
  onEditTemplate?: (template: FoodTemplate) => void
  /** テンプレート削除/編集後にリスト更新を通知 */
  onTemplateChanged?: () => void
  /** テンプレート選択状態 */
  selectedTemplateIds: Set<number>
  onToggleTemplate: (id: number) => void
  templateMultipliers: Map<number, string>
  onTemplateMultiplierChange: (id: number, value: string) => void
}

export function FoodAddPanel({
  openModes,
  onToggleMode,
  onAdd,
  excludeTemplateId,
  onEditTemplate,
  onTemplateChanged,
  selectedTemplateIds,
  onToggleTemplate,
  templateMultipliers,
  onTemplateMultiplierChange,
}: FoodAddPanelProps) {
  return (
    <div className="space-y-2">
      {/* トグルボタン */}
      <div className="flex gap-1">
        <Button
          variant={openModes.has('ai') ? 'default' : 'outline'}
          size="sm"
          className="flex-1 gap-1 text-xs"
          onClick={() => onToggleMode('ai')}
        >
          <Sparkles className="size-3" />
          AI
        </Button>
        <Button
          variant={openModes.has('template') ? 'default' : 'outline'}
          size="sm"
          className="flex-1 gap-1 text-xs"
          onClick={() => onToggleMode('template')}
        >
          <Star className="size-3" />
          お気に入り
        </Button>
        <Button
          variant={openModes.has('db') ? 'default' : 'outline'}
          size="sm"
          className="flex-1 gap-1 text-xs"
          onClick={() => onToggleMode('db')}
        >
          <Database className="size-3" />
          DB検索
        </Button>
      </div>

      {/* 開いているセクション */}
      {openModes.has('ai') && (
        <div className="rounded-md border p-2">
          <AiAddSection onAdd={onAdd} />
        </div>
      )}
      {openModes.has('template') && (
        <div className="rounded-md border p-2">
          <TemplateFavoritesSection
            selectedIds={selectedTemplateIds}
            onToggle={onToggleTemplate}
            multipliers={templateMultipliers}
            onMultiplierChange={onTemplateMultiplierChange}
            excludeTemplateId={excludeTemplateId}
            onEditTemplate={onEditTemplate}
            onTemplateChanged={onTemplateChanged}
          />
        </div>
      )}
      {openModes.has('db') && (
        <div className="rounded-md border p-2">
          <DbSearchSection onAdd={onAdd} />
        </div>
      )}
    </div>
  )
}

// ==============================================================================
// AI追加セクション
// ==============================================================================

function AiAddSection({ onAdd }: { onAdd: (items: MealItemInput[]) => void }) {
  const [text, setText] = useState('')
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const { isSubmitting: busy, run } = useSubmitLock()

  const handleParse = async () => {
    if (!text.trim() && imageFiles.length === 0) return
    await run(async () => {
      try {
        const result = await parseFoodText(text.trim() || undefined, imageFiles[0] || undefined)
        if (result.items.length === 0) {
          toast.error('食品を検出できませんでした')
          return
        }
        onAdd(
          result.items.map((i) => ({
            name: i.name,
            quantity: i.quantity,
            calories: i.calories,
            protein_g: i.protein_g,
            fat_g: i.fat_g,
            carb_g: i.carb_g,
          })),
        )
        toast.success(`${result.items.length}件の食品を追加しました`)
      } catch {
        toast.error('AI解析に失敗しました')
      }
    })
  }

  return (
    <div className="space-y-2">
      <PhotoInput
        label=""
        files={imageFiles}
        previews={imagePreviews}
        onFilesChange={(files, previews) => {
          setImageFiles(files)
          setImagePreviews(previews)
        }}
        maxFiles={1}
      />
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="例: コーヒー、トースト1枚、目玉焼き"
        className="text-xs"
      />
      <Button
        size="sm"
        className="w-full gap-1 text-xs"
        onClick={handleParse}
        disabled={busy || (!text.trim() && imageFiles.length === 0)}
      >
        {busy ? <Loader2 className="size-3 animate-spin" /> : <Sparkles className="size-3" />}
        AI解析して追加
      </Button>
    </div>
  )
}

// ==============================================================================
// DB検索セクション（FoodSearchSection を利用）
// ==============================================================================

function DbSearchSection({ onAdd }: { onAdd: (items: MealItemInput[]) => void }) {
  const [cart, setCart] = useState<FoodCartItem[]>([])

  const handleCartChange = (newCart: FoodCartItem[]) => {
    // 新しく追加されたアイテムを検出して即座に onAdd する
    const added = newCart.filter(
      (item) => !cart.some((c) => c.food_name === item.food_name && c.shop_name === item.shop_name),
    )
    if (added.length > 0) {
      onAdd(
        added.map((food) => ({
          name: food.food_name,
          quantity: food.shop_name ?? undefined,
          calories: food.calories,
          protein_g: food.protein_g,
          fat_g: food.fat_g,
          carb_g: food.carb_g,
        })),
      )
      toast.success(`${added.length}件を追加しました`)
    }
    setCart(newCart)
  }

  return <FoodSearchSection cart={cart} onCartChange={handleCartChange} />
}
